import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createProject,
  deleteProject,
  duplicateProject,
  listGroups,
  listProjects,
  shareProject,
  thumbnailUrl,
  updateProject,
  type Project,
} from "../api";
import { HeroBand } from "../components/HeroBand";
import { Icon } from "../components/Icon";
import { Empty, Modal } from "../components/ui";
import { useToast } from "../lib/toast";
import { PartThumb } from "../scene/PartThumb";

import { DATE_LOCALE, useLang, useT } from "../i18n";
const fmtDate = (iso: string, loc: string) =>
  new Date(iso).toLocaleDateString(loc, { day: "2-digit", month: "short", year: "numeric" });

const NO_GROUP = "Sans groupe";
type Status = "all" | "ready" | "shared" | "empty";
const STATUS: { id: Status; label: string }[] = [
  { id: "all", label: "Tous" },
  { id: "ready", label: "Prêts à imprimer" },
  { id: "shared", label: "Partagés" },
  { id: "empty", label: "Vides" },
];

interface EditState {
  id: string;
  name: string;
  description: string;
  group: string;
}

export function ProjectsPage() {
  const tr = useT();
  const [projects, setProjects] = useState<Project[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<Status>("all");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [group, setGroup] = useState("");
  const [edit, setEdit] = useState<EditState | null>(null);
  const nav = useNavigate();
  const toast = useToast((s) => s.show);

  const load = () => {
    listProjects().then(setProjects).catch((e) => toast(String(e.message), "bad"));
    listGroups().then(setGroups).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  async function create() {
    try {
      const p = await createProject(name, desc, group);
      setCreating(false); setName(""); setDesc(""); setGroup("");
      toast(tr("Projet créé"));
      nav(`/projects/${p.id}`);
    } catch (e) {
      toast(String((e as Error).message), "bad");
    }
  }
  async function saveEdit() {
    if (!edit) return;
    try {
      await updateProject(edit.id, {
        name: edit.name,
        description: edit.description,
        group: edit.group.trim() || null,
        set_group: true,
      });
      setEdit(null); toast(tr("Projet mis à jour")); load();
    } catch (e) {
      toast(String((e as Error).message), "bad");
    }
  }
  async function action(fn: () => Promise<unknown>, ok: string, kind: "ok" | "bad" = "ok") {
    try { await fn(); toast(ok, kind); load(); } catch (e) { toast(String((e as Error).message), "bad"); }
  }

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return projects.filter((p) => {
      if (ql && !(`${p.name} ${p.description ?? ""}`.toLowerCase().includes(ql))) return false;
      if (status === "ready" && !(p.ok_count ?? 0)) return false;
      if (status === "shared" && !p.shared) return false;
      if (status === "empty" && (p.file_count ?? 0) > 0) return false;
      if (groupFilter === NO_GROUP && p.group) return false;
      if (groupFilter !== "all" && groupFilter !== NO_GROUP && p.group !== groupFilter) return false;
      return true;
    });
  }, [projects, q, status, groupFilter]);

  // Group the filtered set into sections (ungrouped last).
  const sections = useMemo(() => {
    const map = new Map<string, Project[]>();
    for (const p of filtered) {
      const g = p.group || NO_GROUP;
      (map.get(g) ?? map.set(g, []).get(g)!).push(p);
    }
    return [...map.entries()].sort((a, b) =>
      a[0] === NO_GROUP ? 1 : b[0] === NO_GROUP ? -1 : a[0].localeCompare(b[0]),
    );
  }, [filtered]);

  return (
    <div>
      <HeroBand
        image="/showcase/h1.jpg"
        eyebrow="Atelier"
        title={tr("Projets")}
        sub="Créez, organisez en groupes, prévisualisez et partagez vos modèles."
        ctaLabel="Nouveau projet"
        ctaIcon="plus"
        onCta={() => setCreating(true)}
      />


      {/* Toolbar: search + status chips + group filter */}
      <div className="row between wrap" style={{ marginBottom: 18, gap: 12 }}>
        <div className="row wrap" style={{ gap: 10 }}>
          <div className="topsearch" style={{ maxWidth: 260 }}>
            <Icon name="search" size={15} />
            <input placeholder={tr("Filtrer les projets…")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="tabs">
            {STATUS.map((s) => (
              <button key={s.id} className={status === s.id ? "on" : ""} onClick={() => setStatus(s.id)}>{tr(s.label)}</button>
            ))}
          </div>
          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} style={{ fontSize: 12.5 }}>
            <option value="all">{tr("Tous les groupes")}</option>
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            <option value={NO_GROUP}>{tr(NO_GROUP)}</option>
          </select>
        </div>
        <span className="muted num" style={{ fontSize: 12 }}>{filtered.length} / {projects.length} {tr("projet(s)")}</span>
      </div>

      {filtered.length === 0 ? (
        <Empty icon="projects">{tr("Aucun projet ne correspond. Crée un projet ou ajuste les filtres.")}</Empty>
      ) : (
        sections.map(([g, items]) => (
          <div key={g} style={{ marginBottom: 26 }}>
            <div className="row" style={{ gap: 9, marginBottom: 13, color: "var(--text-2)" }}>
              <Icon name="layers" size={15} />
              <h2>{g === NO_GROUP ? tr(NO_GROUP) : g}</h2>
              <span className="badge"><span className="num">{items.length}</span></span>
            </div>
            <div className="grid cards" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))" }}>
              {items.map((p) => (
                <ProjectCard
                  key={p.id}
                  p={p}
                  nav={nav}
                  onEdit={() => setEdit({ id: p.id, name: p.name, description: p.description ?? "", group: p.group ?? "" })}
                  onDuplicate={() => action(() => duplicateProject(p.id), tr("Dupliqué"))}
                  onShare={() => action(async () => {
                    const r = await shareProject(p.id);
                    await navigator.clipboard?.writeText(`${location.origin}/shared/${r.share_token}`).catch(() => {});
                  }, tr("Lien de partage copié"))}
                  onDelete={() => action(() => deleteProject(p.id), tr("Supprimé"), "bad")}
                />
              ))}
            </div>
          </div>
        ))
      )}

      {creating && (
        <Modal
          title={tr("Nouveau projet")}
          onClose={() => setCreating(false)}
          footer={<><button className="btn ghost" onClick={() => setCreating(false)}>{tr("Annuler")}</button><button className="btn primary" disabled={!name} onClick={create}>{tr("Créer le projet")}</button></>}
        >
          <div className="field"><label>{tr("Nom")}</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder={tr("Mon support de caméra")} autoFocus /></div>
          <div className="field"><label>{tr("Description")}</label><textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={tr("Optionnel — contexte, contraintes, machine cible…")} /></div>
          <div className="field">
            <label>{tr("Groupe / collection")}</label>
            <input list="pf-groups" value={group} onChange={(e) => setGroup(e.target.value)} placeholder="Optionnel — ex. « Atelier », « Clients »" />
            <datalist id="pf-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
          </div>
        </Modal>
      )}

      {edit && (
        <Modal
          title={tr("Modifier le projet")}
          onClose={() => setEdit(null)}
          footer={<><button className="btn ghost" onClick={() => setEdit(null)}>{tr("Annuler")}</button><button className="btn primary" disabled={!edit.name} onClick={saveEdit}>{tr("Enregistrer")}</button></>}
        >
          <div className="field"><label>{tr("Nom")}</label><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} autoFocus /></div>
          <div className="field"><label>{tr("Description")}</label><textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></div>
          <div className="field">
            <label>{tr("Groupe / collection")}</label>
            <input list="pf-groups" value={edit.group} onChange={(e) => setEdit({ ...edit, group: e.target.value })} placeholder={tr("Vide = sans groupe")} />
            <datalist id="pf-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ProjectCard({
  p,
  nav,
  onEdit,
  onDuplicate,
  onShare,
  onDelete,
}: {
  p: Project;
  nav: (to: string) => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onShare: () => void;
  onDelete: () => void;
}) {
  const tr = useT();
  const { lang } = useLang();
  const files = p.file_count ?? 0;
  const ok = p.ok_count ?? 0;
  return (
    <div className="card hover" style={{ display: "flex", flexDirection: "column", cursor: "default" }}>
      <div className="row" style={{ gap: 14, alignItems: "flex-start" }}>
        <PartThumb material={p.preview_material} passed={p.preview_passed} size={86} empty={files === 0} src={p.preview_file_id ? thumbnailUrl(p.id, p.preview_file_id) : undefined} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="card-row" style={{ marginBottom: 4 }}>
            <a className="card-title" style={{ cursor: "pointer" }} onClick={() => nav(`/projects/${p.id}`)}>{p.name}</a>
            {p.shared && <span className="badge accent">{tr("partagé")}</span>}
          </div>
          <div className="muted" style={{ fontSize: 12.3, lineHeight: 1.45, minHeight: 34, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.description || "—"}</div>
          <div className="row" style={{ gap: 8, marginTop: 8 }}>
            <span className="num muted" style={{ fontSize: 11.5 }}>{files} {tr("fichier(s)")}</span>
            {files > 0 && <span className={`badge ${ok === files ? "ok" : "warn"}`}><span className="dot" /><span className="num">{ok}/{files}</span></span>}
            <span className="muted num" style={{ fontSize: 11, marginLeft: "auto" }}>{fmtDate(p.created_at, DATE_LOCALE[lang])}</span>
          </div>
        </div>
      </div>

      <div className="row between" style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
        <div className="row" style={{ gap: 6 }}>
          <button className="btn sm" onClick={() => nav(`/projects/${p.id}`)}>{tr("Ouvrir")}</button>
          <button className="btn sm ghost" title={tr("Ouvrir dans le Studio 3D")} onClick={() => nav(`/studio?project=${p.id}`)}>
            <Icon name="studio" size={14} /> Studio
          </button>
        </div>
        <div className="row" style={{ gap: 4 }}>
          <button className="btn sm ghost icon" title={tr("Modifier (nom, groupe)")} onClick={onEdit}><Icon name="edit" size={14} /></button>
          <button className="btn sm ghost icon" title={tr("Dupliquer")} onClick={onDuplicate}><Icon name="copy" size={14} /></button>
          <button className="btn sm ghost icon" title={tr("Partager")} onClick={onShare}><Icon name="share" size={14} /></button>
          <button className="btn sm danger icon" title={tr("Supprimer")} onClick={onDelete}><Icon name="trash" size={14} /></button>
        </div>
      </div>
    </div>
  );
}
